import { API_CONFIG } from '../utils/apiConfig';

const ADMIN_API_BASE = API_CONFIG.ADMIN_API_BASE_URL;

class AdminApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = ADMIN_API_BASE;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };
    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
        localStorage.removeItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
        localStorage.removeItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
        window.location.href = '/login';
        throw new Error('Session expired');
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async login(credentials: any) {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getDashboardStats(period?: string) {
    const query = period ? `?period=${period}` : '';
    return this.request(`/analytics/dashboard${query}`);
  }

  async getUserGrowthAnalytics(period: string = '30') {
    return this.request(`/analytics/user-growth?period=${period}`);
  }

  async getProductAnalytics(period: string = '30') {
    return this.request(`/analytics/products?period=${period}`);
  }

  async getRequestAnalytics(period: string = '30') {
    return this.request(`/analytics/requests?period=${period}`);
  }

  async getActivityLogs(params: { page?: number; limit?: number; type?: string } = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/analytics/activity-logs${queryParams ? `?${queryParams}` : ''}`);
  }

  async getTopSearches() {
    return this.request('/analytics/searches/top');
  }

  async getUnmetDemand() {
    return this.request('/analytics/searches/unmet-demand');
  }

  async getTrendingSearchLocations() {
    return this.request('/analytics/searches/trending-locations');
  }

  async getNoResultSearches() {
    return this.request('/analytics/searches/no-results');
  }

  async getUsersByCity() {
    return this.request('/analytics/users-by-city');
  }

  async getUsers(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/users${queryParams ? `?${queryParams}` : ''}`);
  }

  async getUserById(id: string) {
    return this.request(`/users/${id}`);
  }

  async updateUserStatus(id: string, isActive: boolean) {
    return this.request(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  }

  async unsuspendUser(id: string) {
    return this.updateUserStatus(id, true);
  }

  async updateUserVerification(id: string, isVerifiedSeller: boolean) {
    return this.request(`/users/${id}/verification`, {
      method: 'PUT',
      body: JSON.stringify({ isVerifiedSeller }),
    });
  }

  async updateUserPermissions(id: string, permissions: Record<string, Record<string, boolean>>) {
    return this.request(`/permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  }

  async getUserActivity(id: string, params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/users/${id}/activity${queryParams ? `?${queryParams}` : ''}`);
  }

  async deleteUser(id: string) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async getUserStats() {
    return this.request('/users/stats');
  }

  async getUserReviews(userId: string, params: any = {}) {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const queryParams = new URLSearchParams(
      Object.entries(params).reduce((acc: any, [k, v]) => {
        if (v !== undefined && v !== '') acc[k] = String(v);
        return acc;
      }, {})
    ).toString();
    const url = `${API_CONFIG.BASE_URL}/products/user/${userId}/reviews${queryParams ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async getProducts(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/products${queryParams ? `?${queryParams}` : ''}`);
  }

  async getProductById(id: string) {
    return this.request(`/products/${id}`);
  }

  async updateProductStatus(id: string, data: { status?: string; isActive?: boolean }) {
    return this.request(`/products/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateProductAdmin(id: string, data: any) {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string) {
    return this.request(`/products/${id}`, { method: 'DELETE' });
  }

  async getFlaggedProducts(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/products/flagged${queryParams ? `?${queryParams}` : ''}`);
  }

  async getProductStats() {
    return this.request('/products/stats');
  }

  async getRequests(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/requests${queryParams ? `?${queryParams}` : ''}`);
  }

  async getRequestById(id: string) {
    return this.request(`/requests/${id}`);
  }

  async updateRequestStatus(id: string, status: string) {
    return this.request(`/requests/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteRequest(id: string) {
    return this.request(`/requests/${id}`, { method: 'DELETE' });
  }

  async getFlaggedRequests(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/requests/flagged${queryParams ? `?${queryParams}` : ''}`);
  }

  async getRequestStats() {
    return this.request('/requests/stats');
  }

  async getReportedIssues(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/reported-issues${queryParams ? `?${queryParams}` : ''}`);
  }

  async getReportedIssueById(id: string) {
    return this.request(`/reported-issues/${id}`);
  }

  async updateReportedIssueStatus(id: string, status: string) {
    return this.request(`/reported-issues/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getUserPermissions(userId: string) {
    return this.request(`/permissions/${userId}`);
  }

  async resetUserPermissions(userId: string) {
    return this.request(`/permissions/${userId}`, { method: 'DELETE' });
  }

  async getChats(params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/chat${queryParams ? `?${queryParams}` : ''}`);
  }

  async getChatStats() {
    return this.request('/chat/stats');
  }

  async getDisputeConversations(params: any) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/chat/disputes?${queryParams}`);
  }

  async getConversationsByProduct(productId: string, params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/chat/product/${productId}${queryParams ? `?${queryParams}` : ''}`);
  }

  async getConversationById(id: string) {
    return this.request(`/chat/${id}`);
  }

  async getChatMessages(conversationId: string, params: any = {}) {
    const queryParams = new URLSearchParams(params as any).toString();
    return this.request(`/chat/${conversationId}/messages${queryParams ? `?${queryParams}` : ''}`);
  }

  async sendChatMessage(conversationId: string, data: any) {
    return this.request(`/chat/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConversationStatus(id: string, isArchived: boolean) {
    return this.request(`/chat/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isArchived }),
    });
  }

  async deleteConversation(id: string) {
    return this.request(`/chat/${id}`, { method: 'DELETE' });
  }

  async getProductReviews(productId: string, params: any = {}) {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const queryParams = new URLSearchParams(
      Object.entries(params).reduce((acc: any, [k, v]) => {
        if (v !== undefined && v !== '') acc[k] = String(v);
        return acc;
      }, {})
    ).toString();
    const url = `${API_CONFIG.BASE_URL}/products/${productId}/reviews${queryParams ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async exportData(type: string) {
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    const response = await fetch(`${this.baseUrl}/export/${type}`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_export.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const adminApiService = new AdminApiService();
